import { c as _c } from "react/compiler-runtime";
import Editor, { type OnMount } from '@monaco-editor/react';
import { cx } from 'class-variance-authority';
import type * as monacoTypes from 'monaco-editor';
import { forwardRef, useEffect, useImperativeHandle, useRef, useState } from 'react';
import { Shimmer } from '../..';
import useIsDarkMode from '../../hooks/use-is-dark-mode';
import { useMonaco } from '../monaco/monaco-provider';
import { getVariantSpecificMonacoConfig } from '../monaco/utils';
import type { TemplateInputProps } from '../template-input/template-input';
import { getJsonThemeName } from './utils/monaco-config';
import { moveFocus } from './utils/template-editor-utils';
import { modelAutocomplete } from './utils/template-utils';
export type TemplateEditorProps = {
  id?: string;
  value?: string;
  onChange?: (val: string) => void;
  height?: number | string;
  disabled?: boolean;
  autocompleteItems: TemplateInputProps['autocompleteItems'];
  placeholder?: string;
  size?: 'default' | 'compact';
  variant?: 'textarea' | 'input';
  className?: string;
  hasError?: boolean;
  onFocus?: () => void;
  onBlur?: () => void;
  autoFocus?: boolean;
  bottomPadding?: number;
};
export type TemplateEditorRef = {
  focus: () => void;
};
const TemplateEditor = forwardRef<TemplateEditorRef, TemplateEditorProps>((t0, ref) => {
  const $ = _c(53);
  const {
    id,
    value: t1,
    onChange,
    height: t2,
    disabled: t3,
    autocompleteItems,
    placeholder,
    size: t4,
    variant: t5,
    className,
    hasError,
    onFocus,
    onBlur,
    autoFocus: t6,
    bottomPadding
  } = t0;
  const value = t1 === undefined ? "" : t1;
  const height = t2 === undefined ? 150 : t2;
  const disabled = t3 === undefined ? false : t3;
  const size = t4 === undefined ? "compact" : t4;
  const variant = t5 === undefined ? "textarea" : t5;
  const autoFocus = t6 === undefined ? false : t6;
  const {
    monaco
  } = useMonaco();
  const isDarkMode = useIsDarkMode();
  let t7;
  if ($[0] !== isDarkMode) {
    t7 = getJsonThemeName(isDarkMode);
    $[0] = isDarkMode;
    $[1] = t7;
  } else {
    t7 = $[1];
  }
  const themeName = t7;
  const [isFocused, setIsFocused] = useState(false);
  const [isEditorMounted, setIsEditorMounted] = useState(false);
  const mountedModelRef = useRef(null);
  const editorRef = useRef(null);
  const containerRef = useRef(null);
  let t8;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t8 = () => ({
      focus: () => {
        editorRef.current?.focus();
      }
    });
    $[2] = t8;
  } else {
    t8 = $[2];
  }
  useImperativeHandle(ref, t8);
  const editorHeight = variant === "input" ? size === "compact" ? 32 : 40 : height;
  let t9;
  if ($[3] !== autoFocus || $[4] !== autocompleteItems || $[5] !== disabled || $[6] !== monaco || $[7] !== onBlur || $[8] !== onFocus || $[9] !== variant) {
    t9 = editor => {
      if (!monaco) {
        return;
      }
      editorRef.current = editor;
      if (autoFocus && !disabled) {
        requestAnimationFrame(() => {
          editor.focus();
          const m = editor.getModel();
          if (m) {
            const lineNumber = m.getLineCount();
            const column = m.getLineMaxColumn(lineNumber);
            editor.setPosition({
              lineNumber,
              column
            });
            editor.revealPositionInCenterIfOutsideViewport({
              lineNumber,
              column
            });
          }
        });
      }
      const model = editor.getModel();
      if (model) {
        mountedModelRef.current = model;
        modelAutocomplete.set(model, autocompleteItems ?? {});
      }
      editor.onDidChangeModel(e => {
        if (e.oldModelUrl && mountedModelRef.current) {
          modelAutocomplete.delete(mountedModelRef.current);
        }
        const newModel = editor.getModel();
        mountedModelRef.current = newModel ?? null;
        if (newModel) {
          modelAutocomplete.set(newModel, autocompleteItems ?? {});
        }
      });
      const templateVariableDecorationType = editor.createDecorationsCollection();
      const highlightTemplateVariables = () => {
        const m_0 = editor.getModel();
        if (!m_0) {
          return;
        }
        const text = m_0.getValue();
        const decorations = [];
        const completeRegex = /\{\{\s*[^}]+\s*\}\}/g;
        const unclosedRegex = /\{\{(?![^{]*\}\}).*$/gm;
        let match;
        while ((match = completeRegex.exec(text)) !== null) {
          const startPos = m_0.getPositionAt(match.index);
          const endPos = m_0.getPositionAt(match.index + match[0].length);
          decorations.push({
            range: new monaco.Range(startPos.lineNumber, startPos.column, endPos.lineNumber, endPos.column),
            options: {
              className: "template-variable-decoration",
              inlineClassName: "template-variable-inline",
              stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges
            }
          });
        }
        while ((match = unclosedRegex.exec(text)) !== null) {
          const startPos_0 = m_0.getPositionAt(match.index);
          const endPos_0 = m_0.getPositionAt(match.index + match[0].length);
          decorations.push({
            range: new monaco.Range(startPos_0.lineNumber, startPos_0.column, endPos_0.lineNumber, endPos_0.column),
            options: {
              className: "template-variable-decoration",
              inlineClassName: "template-variable-inline",
              stickiness: monaco.editor.TrackedRangeStickiness.NeverGrowsWhenTypingAtEdges
            }
          });
        }
        templateVariableDecorationType.set(decorations);
      };
      editor.onDidChangeModelContent(() => {
        requestAnimationFrame(() => {
          const m_1 = editor.getModel();
          if (!m_1) {
            return;
          }
          const position = editor.getPosition();
          if (!position) {
            return;
          }
          const line = m_1.getLineContent(position.lineNumber);
          const beforeCursor = line.substring(0, position.column - 1);
          const openIdx = beforeCursor.lastIndexOf("{{");
          if (openIdx !== -1) {
            const between = beforeCursor.substring(openIdx + 2);
            if (!between.includes("}}")) {
              const isSuggestVisible = !!document.querySelector(".suggest-widget.visible");
              if (!isSuggestVisible) {
                editor.trigger("template-editor", "editor.action.triggerSuggest", {});
              }
            }
          }
        });
      });
      if (variant === "input") {
        editor.onKeyDown(e_0 => {
          if (e_0.keyCode === monaco.KeyCode.Enter) {
            const isSuggestVisible_0 = !!document.querySelector(".suggest-widget.visible");
            if (!isSuggestVisible_0) {
              e_0.preventDefault();
              e_0.stopPropagation();
            }
          }
          if (e_0.keyCode === monaco.KeyCode.Tab) {
            const isSuggestVisible_1 = !!document.querySelector(".suggest-widget.visible");
            if (!isSuggestVisible_1) {
              e_0.preventDefault();
              e_0.stopPropagation();
              moveFocus(!e_0.shiftKey);
            }
          }
        });
        editor.onDidPaste(() => {
          const m_2 = editor.getModel();
          if (!m_2) {
            return;
          }
          const text_0 = m_2.getValue();
          const singleLineText = text_0.replace(/\r?\n|\r/g, " ");
          if (text_0 !== singleLineText) {
            m_2.setValue(singleLineText);
            const lineCount = m_2.getLineCount();
            const lastLineLength = m_2.getLineLength(lineCount);
            editor.setPosition({
              lineNumber: lineCount,
              column: lastLineLength + 1
            });
          }
        });
      }
      editor.onDidFocusEditorText(() => {
        setIsFocused(true);
        onFocus?.();
      });
      editor.onDidBlurEditorText(() => {
        setIsFocused(false);
        onBlur?.();
      });
      editor.onDidFocusEditorWidget(() => {
        setIsFocused(true);
        onFocus?.();
      });
      editor.onDidBlurEditorWidget(() => {
        setIsFocused(false);
        onBlur?.();
      });
      highlightTemplateVariables();
      editor.onDidChangeModelContent(() => highlightTemplateVariables());
      editor.onDidChangeModelLanguage(() => {
        setTimeout(highlightTemplateVariables, 0);
      });
      setIsEditorMounted(true);
    };
    $[3] = autoFocus;
    $[4] = autocompleteItems;
    $[5] = disabled;
    $[6] = monaco;
    $[7] = onBlur;
    $[8] = onFocus;
    $[9] = variant;
    $[10] = t9;
  } else {
    t9 = $[10];
  }
  const onMount = t9;
  let t10;
  let t11;
  if ($[11] !== autocompleteItems) {
    t10 = () => {
      const model_0 = mountedModelRef.current;
      if (model_0) {
        modelAutocomplete.set(model_0, autocompleteItems ?? {});
      }
    };
    t11 = [autocompleteItems];
    $[11] = autocompleteItems;
    $[12] = t10;
    $[13] = t11;
  } else {
    t10 = $[12];
    t11 = $[13];
  }
  useEffect(t10, t11);
  if (!monaco) {
    let t12;
    if ($[14] !== editorHeight) {
      t12 = <Shimmer className="w-full" height={editorHeight} />;
      $[14] = editorHeight;
      $[15] = t12;
    } else {
      t12 = $[15];
    }
    return t12;
  }
  let t12;
  if ($[16] === Symbol.for("react.memo_cache_sentinel")) {
    t12 = () => {
      editorRef.current?.focus();
    };
    $[16] = t12;
  } else {
    t12 = $[16];
  }
  const handleFocusProxy = t12;
  const t13 = !isEditorMounted;
  let t14;
  if ($[17] !== id) {
    t14 = id && <input id={id} className="sr-only" onFocus={handleFocusProxy} tabIndex={-1} aria-hidden="true" />;
    $[17] = id;
    $[18] = t14;
  } else {
    t14 = $[18];
  }
  const t15 = !disabled;
  const t16 = !hasError && isFocused;
  let t17;
  if ($[19] !== className || $[20] !== disabled || $[21] !== hasError || $[22] !== t15 || $[23] !== t16) {
    t17 = cx("\n            relative w-full rounded-md border border-primary\n            outline-none\n            hover:border-(--border-color-primary-hover)\n            px-4\n            [&_.monaco-editor]:absolute!\n          ", {
      "bg-primary [&_.monaco-editor-background]:bg-primary!": t15,
      "bg-secondary! border-tertiary! opacity-60 [&_.monaco-editor-background]:bg-secondary!": disabled,
      "border-secondary! shadow-focus-accent": t16,
      "border-error!": hasError,
      "shadow-focus-error hover:shadow-none": hasError
    }, className);
    $[19] = className;
    $[20] = disabled;
    $[21] = hasError;
    $[22] = t15;
    $[23] = t16;
    $[24] = t17;
  } else {
    t17 = $[24];
  }
  let t18;
  if ($[25] !== onChange) {
    t18 = v => onChange?.(v ?? "");
    $[25] = onChange;
    $[26] = t18;
  } else {
    t18 = $[26];
  }
  let t19;
  if ($[27] === Symbol.for("react.memo_cache_sentinel")) {
    t19 = <Shimmer className="w-full h-full" />;
    $[27] = t19;
  } else {
    t19 = $[27];
  }
  let t20;
  bb0: {
    let t21;
    if ($[28] !== disabled || $[29] !== placeholder || $[30] !== size || $[31] !== variant) {
      t21 = getVariantSpecificMonacoConfig({
        disabled,
        size,
        placeholder,
        variant
      });
      $[28] = disabled;
      $[29] = placeholder;
      $[30] = size;
      $[31] = variant;
      $[32] = t21;
    } else {
      t21 = $[32];
    }
    const baseConfig = t21;
    if (bottomPadding !== undefined && variant === "textarea") {
      let t22;
      if ($[33] !== baseConfig.padding) {
        t22 = baseConfig.padding ?? {};
        $[33] = baseConfig.padding;
        $[34] = t22;
      } else {
        t22 = $[34];
      }
      let t23;
      if ($[35] !== bottomPadding || $[36] !== t22) {
        t23 = {
          ...t22,
          bottom: bottomPadding
        };
        $[35] = bottomPadding;
        $[36] = t22;
        $[37] = t23;
      } else {
        t23 = $[37];
      }
      let t24;
      if ($[38] !== baseConfig || $[39] !== t23) {
        t24 = {
          ...baseConfig,
          padding: t23
        };
        $[38] = baseConfig;
        $[39] = t23;
        $[40] = t24;
      } else {
        t24 = $[40];
      }
      t20 = t24;
      break bb0;
    }
    t20 = baseConfig;
  }
  let t21;
  if ($[41] !== editorHeight || $[42] !== onMount || $[43] !== t17 || $[44] !== t18 || $[45] !== t20 || $[46] !== themeName || $[47] !== value) {
    t21 = <Editor theme={themeName} className={t17} height={editorHeight} language="template" value={value} onChange={t18} onMount={onMount} loading={t19} options={t20} />;
    $[41] = editorHeight;
    $[42] = onMount;
    $[43] = t17;
    $[44] = t18;
    $[45] = t20;
    $[46] = themeName;
    $[47] = value;
    $[48] = t21;
  } else {
    t21 = $[48];
  }
  let t22;
  if ($[49] !== t13 || $[50] !== t14 || $[51] !== t21) {
    t22 = <div ref={containerRef} className="template-editor-container relative" aria-busy={t13}>{t14}{t21}</div>;
    $[49] = t13;
    $[50] = t14;
    $[51] = t21;
    $[52] = t22;
  } else {
    t22 = $[52];
  }
  return t22;
});
TemplateEditor.displayName = 'TemplateEditor';
export default TemplateEditor;